import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";
import { Hammer, Music, Star, MapPin, ArrowRight, Drama, CalendarDays, Sparkles, Megaphone, Users, Search, Tent } from "lucide-react";
import EventImage from "@/components/EventImage";

const iconFor = (type) => (type === "band" ? Music : type === "troupe" ? Drama : Hammer);

const ClanCard = ({ e, i, t }) => {
  const Icon = iconFor(e.type);
  const boosted = !!e.featured;
  const sponsored = e.feature_type === "sponsored";
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: (i % 6) * 0.06, ease: "easeOut" }}
    >
      <Link
        to={e.profile_path}
        data-testid={`directory-card-${e.slug}`}
        className={`group relative flex flex-col justify-between h-full rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1.5 ${
          boosted
            ? "bg-gradient-to-br from-[#181A20] via-[#121418] to-[#1E1710] border-2 border-amber-500/60 ember-glow shadow-[0_0_25px_rgba(217,119,6,0.16)]"
            : "bg-[#121418] border border-[#272A34] hover:border-amber-500/50"
        }`}
      >
        {boosted && (
          <span
            data-testid={sponsored ? "feature-badge-sponsored" : "feature-badge-editorial"}
            className={`absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest font-bold shadow-lg ${
              sponsored
                ? "bg-stone-800/90 text-stone-200 border border-stone-600"
                : "bg-amber-500 text-stone-950 border border-amber-300 shadow-amber-950/70"
            }`}
          >
            {sponsored ? <Megaphone className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
            {sponsored ? t("dir_sponsored") : t("dir_discover")}
          </span>
        )}
        <div className="relative h-56 overflow-hidden bg-stone-900">
          <EventImage src={e.image} alt={e.name} imgClassName="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E] via-[#0B0C0E]/30 to-transparent opacity-90" />
          {e.has_historical_tent && <span data-testid={`dir-tent-${e.slug}`} className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase bg-amber-950/80 text-amber-200 border border-amber-800/50 backdrop-blur-sm"><Tent className="w-3 h-3" /> Tente historique</span>}
          {e.craft && (
            <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono uppercase bg-amber-950/80 text-amber-300 border border-amber-800/50 backdrop-blur-sm">
              <Icon className="w-3.5 h-3.5" /> {e.craft}
            </span>
          )}
          {e.upcoming_events > 0 && (
            <span data-testid={`dir-upcoming-${e.slug}`} className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-mono bg-emerald-950/80 text-emerald-300 border border-emerald-700/50 backdrop-blur-sm">
              <CalendarDays className="w-3 h-3" /> {e.upcoming_events} {t("dir_coming")}
            </span>
          )}
        </div>
        <div className="p-6 flex-1 flex flex-col">
          <h3 className="font-serif text-xl text-stone-100 group-hover:text-amber-200 transition-colors mb-2 line-clamp-1">{e.name}</h3>
          <p className="text-sm text-stone-400 line-clamp-2 leading-relaxed mb-4">{e.bio}</p>
          <div className="flex items-center justify-between border-t border-[#272A34]/60 pt-4 mt-auto">
            <span className="flex items-center gap-1.5 text-xs text-stone-400 font-mono uppercase tracking-wider">
              <MapPin className="w-3.5 h-3.5 text-amber-600/80" /> {e.region || "Suisse"}
            </span>
            <ArrowRight className="w-4 h-4 text-amber-500 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default function Directory() {
  const { t } = useLang();
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [canton, setCanton] = useState("");
  const { data: cantons = [] } = useQuery({ queryKey: ["clanCantons"], queryFn: async () => (await api.get("/clan/cantons")).data });
  const params = { ...(type ? { type } : {}), ...(q ? { q } : {}), ...(canton ? { canton } : {}) };
  const { data: entries = [], isLoading } = useQuery({
    queryKey: ["clan", type, q, canton],
    queryFn: async () => (await api.get("/clan", { params })).data,
  });

  const tabs = [
    { key: "", label: t("dir_all"), icon: Users },
    { key: "artisan", label: t("dir_artisans"), icon: Hammer },
    { key: "band", label: t("dir_bands"), icon: Music },
    { key: "troupe", label: t("dir_troupes"), icon: Drama },
  ];

  return (
    <>
      <Seo title={t("directory_title")} description={t("directory_subtitle")} canonicalPath="/directory" />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-amber-900/30">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 25% 15%, rgba(217,119,6,0.14), rgba(11,12,14,0.9) 55%, #0B0C0E 90%)" }} />
        <div className="absolute inset-0 noise-bg opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 sm:py-20">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-800/40 bg-amber-950/30 backdrop-blur mb-6">
              <Star className="w-3.5 h-3.5 text-amber-500" />
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-400">swiss-pagan.ch</span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight text-amber-100 text-glow leading-[0.98]">{t("directory_title")}</h1>
            <p className="text-stone-300/90 mt-5 max-w-2xl text-base sm:text-lg leading-relaxed">{t("directory_subtitle")}</p>
            <Link to="/directory/join" data-testid="directory-join-cta" className="inline-flex items-center gap-2 mt-7 px-6 py-3 rounded-full text-sm font-semibold bg-gradient-to-r from-amber-600 via-amber-500 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-stone-950 border border-amber-300/40 shadow-[0_0_20px_rgba(217,119,6,0.3)] hover:shadow-[0_0_30px_rgba(217,119,6,0.5)] transition-all">
              <Star className="w-4 h-4" /> Figurer dans l'annuaire — CHF 99/an
            </Link>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        {/* SEARCH + CANTON */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-stone-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input data-testid="clan-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("clan_search_ph")}
              className="w-full bg-[#121418] border border-[#272A34] rounded-full pl-10 pr-4 py-2.5 text-sm text-stone-200 outline-none focus:border-amber-500/50 transition-colors" />
          </div>
          <select data-testid="clan-canton" value={canton} onChange={(e) => setCanton(e.target.value)}
            className="bg-[#121418] border border-[#272A34] rounded-full px-4 py-2.5 text-sm text-stone-200 outline-none focus:border-amber-500/50">
            <option value="">{t("clan_all_cantons")}</option>
            {cantons.map((c) => <option key={c.key} value={c.key}>{c.key}</option>)}
          </select>
        </div>

        {/* FILTER TABS */}
        <div className="flex flex-wrap gap-2.5 mb-10">
          {tabs.map((tab) => {
            const TIcon = tab.icon;
            const active = type === tab.key;
            return (
              <button key={tab.key} data-testid={`directory-tab-${tab.key || "all"}`} onClick={() => setType(tab.key)}
                className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm transition-all ${active ? "bg-amber-600 text-stone-950 font-bold shadow-md shadow-amber-900/40" : "bg-[#121418] text-stone-300 border border-[#272A34] hover:border-amber-500/40 hover:text-amber-200"}`}>
                <TIcon className="w-4 h-4" /> {tab.label}
              </button>
            );
          })}
        </div>

        {isLoading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{[...Array(6)].map((_, i) => <div key={i} className="h-96 bg-[#121418] border border-[#272A34] rounded-xl animate-pulse" />)}</div>
        ) : entries.length === 0 ? (
          <div className="text-center py-20 text-stone-400 border border-dashed border-[#272A34] rounded-xl" data-testid="directory-empty">{t("dir_empty")}</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="directory-grid">
            {entries.map((e, i) => <ClanCard key={`${e.source}-${e.slug}`} e={e} i={i} t={t} />)}
          </div>
        )}
      </div>
    </>
  );
}
